import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClientApisProvider } from '@d-orca/shared/client/hooks';
import { App } from '@src/App';
import { RateLimitNotice } from '@components/RateLimitNotice/RateLimitNotice';
import { ToastProvider } from '@components/ui/Toasts/ToastProvider';
import { MutationErrorBackstop } from '@components/ui/Toasts/MutationErrorBackstop';
import { clientApis } from '@apis/clientApis';
import { store } from '@store/store';
import { providerIssueAction } from '@store/slices/providerStatus.fromError';
import '@src/i18n';
import '@src/styles/reset.css';
import '@src/styles/tokens.css';
import '@src/styles/global.css';

// react-grid-layout bundles react-draggable / react-resizable, whose `log()`
// helper reads `process.env.DRAGGABLE_DEBUG` on every drag/resize start. The
// browser has no `process`, so that threw "process is not defined" *inside* the
// mousedown handler and silently killed all drag/resize (cards still rendered).
// vite.config's `define` replaces the token at transform time (dev AND build),
// but only in code Vite has re-bundled — this runtime shim is the belt-and-
// braces fallback for any pre-optimized dependency chunk that still carries the
// bare `process` reference (e.g. a stale dep cache).
(globalThis as typeof globalThis & { process?: { env: Record<string, string | undefined> } })
  .process ??= { env: {} };

const queryClient = new QueryClient({
  // Reactive reconnect detection (the source of truth): any query that fails
  // with a provider error flips the global reconnect chip via the providerStatus
  // slice (the TopBar reads it). Fires the moment a real request hits a dead or
  // absent provider session — covering every provider-backed page — so there's
  // no need to poll a status endpoint to discover it.
  queryCache: new QueryCache({
    onError: (error) => {
      const action = providerIssueAction(error);
      if (action) store.dispatch(action);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element not found');
createRoot(rootEl).render(
  <StrictMode>
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <ClientApisProvider apis={clientApis}>
          {/* The shared toast stack every write reports its outcome on. */}
          <ToastProvider>
            <App />
            {/* App-wide: explains a 429 from any authenticated call. */}
            <RateLimitNotice />
            {/* App-wide: a failed write that reports nothing itself still toasts. */}
            <MutationErrorBackstop />
          </ToastProvider>
        </ClientApisProvider>
      </QueryClientProvider>
    </ReduxProvider>
  </StrictMode>,
);
